/** CRUD de utilizadores — mesma API S2 do Financeiro (`/api/v1/usuarios`). */
import {
  defaultPaineisForTipo,
  normalizePaineis,
  type PaineisMap,
} from '../permissions/panels'
import { resetSessaoFinanceira } from './bridge'

const API_BASE = (import.meta.env.VITE_FINANCIAL_API_BASE as string) || '/api/v1'

export type UserTipo = 'admin' | 'usuario' | 'leitura'

export type BiUser = {
  id: string
  nome: string
  email: string
  tipo: UserTipo
  ativo: boolean
  whatsapp: string
  paineis: PaineisMap
  data_criacao: string
  createdAt: string
  updatedAt: string
  ultimo_login?: string | null
}

export type UserFormData = {
  nome: string
  email: string
  senha?: string
  whatsapp?: string
  tipo: UserTipo
  ativo: boolean
  paineis: PaineisMap
}

export type UserFilters = {
  search?: string
  ativo?: boolean
  page?: number
  limit?: number
}

export type UserListResponse = {
  usuarios: BiUser[]
  paginacao: { page: number; limit: number; total: number; pages: number }
}

function apiRoot() {
  return API_BASE.replace(/\/$/, '')
}

async function garantirSessao(): Promise<void> {
  const res = await fetch(`${apiRoot()}/auth/check`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store',
  })
  if (!res.ok) {
    resetSessaoFinanceira()
    throw new Error(`Sessão inválida (HTTP ${res.status})`)
  }
}

function mapUsuarioFromApi(raw: Record<string, unknown>): BiUser {
  const id = raw._id != null ? String(raw._id) : String(raw.id ?? '')
  const toIso = (v: unknown): string | undefined => {
    if (v == null || v === '') return undefined
    const t = new Date(v as string | Date).getTime()
    if (Number.isNaN(t)) return undefined
    return new Date(t).toISOString()
  }
  const isoDc = toIso(raw.data_criacao) ?? new Date().toISOString()
  const isoDu = toIso(raw.data_atualizacao) ?? isoDc
  const tipo: UserTipo =
    raw.tipo === 'admin' || raw.tipo === 'leitura' ? (raw.tipo as UserTipo) : 'usuario'
  return {
    id,
    nome: (raw.nome as string) ?? '',
    email: (raw.email as string) ?? '',
    tipo,
    ativo: Boolean(raw.ativo),
    whatsapp: typeof raw.whatsapp === 'string' ? raw.whatsapp : '',
    paineis: normalizePaineis(raw.paineis, tipo),
    data_criacao: isoDc,
    createdAt: isoDc,
    updatedAt: isoDu,
    ultimo_login: toIso(raw.ultimo_login) ?? null,
  }
}

function unwrapError(json: unknown, fallback: string, status: number): string {
  const j = json as { error?: { message?: string } | string; message?: string }
  if (typeof j?.error === 'string') return j.error
  if (j?.error && typeof j.error === 'object' && j.error.message) return j.error.message
  if (j?.message) return j.message
  return `${fallback} (HTTP ${status})`
}

async function apiJson<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  await garantirSessao()
  const res = await fetch(`${apiRoot()}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    credentials: 'include',
    cache: 'no-store',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean
    data?: T
    error?: { message?: string } | string
  }
  if (!res.ok || json.success === false) {
    throw new Error(unwrapError(json, 'Erro na API de utilizadores', res.status))
  }
  if (json.data !== undefined) return json.data as T
  return json as unknown as T
}

export async function listUsers(filters: UserFilters = {}): Promise<UserListResponse> {
  const params = new URLSearchParams()
  if (filters.search) params.append('search', filters.search)
  if (filters.ativo !== undefined) params.append('ativo', String(filters.ativo))
  if (filters.page) params.append('page', String(filters.page))
  if (filters.limit) params.append('limit', String(filters.limit))
  const qs = params.toString()
  const data = await apiJson<{
    usuarios?: Record<string, unknown>[]
    paginacao?: UserListResponse['paginacao']
  }>('GET', `/usuarios${qs ? `?${qs}` : ''}`)
  if (!data?.usuarios || !data?.paginacao) {
    throw new Error('Resposta de listagem de usuários inválida')
  }
  return {
    usuarios: data.usuarios.map((u) => mapUsuarioFromApi(u)),
    paginacao: data.paginacao,
  }
}

export async function getUserById(id: string): Promise<BiUser> {
  const data = await apiJson<Record<string, unknown>>('GET', `/usuarios/${id}`)
  return mapUsuarioFromApi(data)
}

export type CreateUserResult = {
  user: BiUser
  conviteEnviado: boolean
  /** wa.me com a mensagem pronta (só quando há convite e WhatsApp cadastrado). */
  whatsappUrl: string | null
  message?: string
}

export async function createUser(payload: {
  nome: string
  email: string
  senha?: string
  whatsapp?: string
  tipo: UserTipo
  ativo: boolean
  paineis?: PaineisMap
}): Promise<CreateUserResult> {
  await garantirSessao()
  const body: Record<string, unknown> = {
    nome: payload.nome,
    email: payload.email,
    tipo: payload.tipo,
    ativo: payload.ativo,
    paineis:
      payload.tipo === 'admin'
        ? null
        : payload.paineis ?? defaultPaineisForTipo(payload.tipo),
  }
  if (payload.senha) body.senha = payload.senha
  if (payload.whatsapp) body.whatsapp = payload.whatsapp

  const res = await fetch(`${apiRoot()}/usuarios`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean
    data?: Record<string, unknown>
    conviteEnviado?: boolean
    whatsappUrl?: string | null
    message?: string
    error?: { message?: string } | string
  }
  if (!res.ok || json.success === false || !json.data) {
    throw new Error(unwrapError(json, 'Erro ao criar utilizador', res.status))
  }
  return {
    user: mapUsuarioFromApi(json.data),
    conviteEnviado: Boolean(json.conviteEnviado),
    whatsappUrl: json.whatsappUrl ?? null,
    message: json.message,
  }
}

export type ResendInviteResult = {
  message?: string
  /** wa.me com a mensagem pronta e o MESMO link do e-mail (null se não há WhatsApp salvo). */
  whatsappUrl: string | null
}

/** Admin: gera um link novo, envia por e-mail e devolve o wa.me com esse mesmo link. */
export async function resendInvite(id: string): Promise<ResendInviteResult> {
  const json = await apiJson<{ message?: string; whatsappUrl?: string | null }>(
    'POST',
    `/usuarios/${id}/convite`,
    { canal: 'email' },
  )
  return { message: json.message, whatsappUrl: json.whatsappUrl ?? null }
}

export async function updateUser(
  id: string,
  payload: {
    nome?: string
    email?: string
    senha?: string
    whatsapp?: string
    tipo?: UserTipo
    ativo?: boolean
    paineis?: PaineisMap | null
  },
): Promise<BiUser> {
  const data = await apiJson<Record<string, unknown>>('PUT', `/usuarios/${id}`, payload)
  return mapUsuarioFromApi(data)
}

export async function deleteUser(id: string): Promise<void> {
  await apiJson<{ message?: string }>('DELETE', `/usuarios/${id}`)
}

export function formatUserDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('pt-BR')
}

export function formatUserDateTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function perfilLabel(tipo?: string): string {
  if (tipo === 'admin') return 'Administrador'
  if (tipo === 'leitura') return 'Somente leitura'
  return 'Operador'
}
